/* PlannerSubmissionRecords — iter98.99br
 *
 * Replaces the previous loose "Scans & X-rays" + "Photos" cards with
 * a SINGLE card containing tabs (Photos | 3D Scans | Documents).
 * Visual treatment mirrors the section style used elsewhere on the
 * planner's Submission tab (3px primary left-bar accent in the
 * header). Each tab shows file thumbnails with name + size + content
 * type metadata. Photos render image thumbnails directly; STL files
 * and documents render an icon-only thumb.
 */
import React, { useState, useMemo, useEffect } from "react";
import { Cube, Image as ImageIcon, Camera, FileText, ArrowsClockwise, CaretDown } from "@phosphor-icons/react";
import { API } from "@/lib/api";


const FILE_KIND_LABEL = {
    intraoral_front: "Intraoral Front",
    intraoral_anterior_rest: "Intraoral Front (at rest)",
    intraoral_left: "Intraoral Left",
    intraoral_right: "Intraoral Right",
    intraoral_upper_occlusal: "Intraoral Upper Occlusal",
    intraoral_lower_occlusal: "Intraoral Lower Occlusal",
    extraoral_front: "Extraoral Front",
    extraoral_profile: "Extraoral Left Profile",
    extraoral_profile_right: "Extraoral Right Profile",
    extraoral_smile: "Extraoral Smile",
    xray_opg: "OPG X-Ray",
    xray_lateral_ceph: "Lateral Cephalogram",
    cbct: "CBCT",
    scan_upper: "Upper Arch Scan",
    scan_lower: "Lower Arch Scan",
    scan_bite: "Bite Registration",
    medical_history_form: "Medical History Form",
    consent_form: "Consent Form",
};


export default function PlannerSubmissionRecords({
    scans = [], photos = [], xrays = [], documents = [],
    newSinceInfoRequest = null,
    caseId = null,
    number,
}) {
    const total = scans.length + photos.length + xrays.length + documents.length;
    const [tab, setTab] = useState(() => {
        if (photos.length > 0) return "photos";
        if (scans.length > 0) return "scans";
        if (xrays.length > 0) return "xrays";
        return "docs";
    });

    /* iter98.99bt — accordion collapse parity with the brief sections.
       Persisted per-case via localStorage. Defaults COLLAPSED so the
       planner's Submission tab never shows a "wall of expanded cards"
       on first open — user has explicitly requested collapsed-by-default
       across every accordion in the app multiple times.
       iter98.99do — bumped key from `plannerRecords:` to `plannerRecords2:`
       to invalidate stale `=1` (open) entries left over from when the
       default was `true`. Anyone who opened this card before the
       collapsed-by-default fix shipped will still get a fresh closed
       state on next page load. */
    const storageKey = `plannerRecords2:${caseId || "default"}`;
    const [open, setOpen] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            return v === null ? false : v === "1";
        } catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* noop */ }
    }, [open, storageKey]);

    const tabs = useMemo(() => [
        { id: "photos", label: "Photos", icon: Camera, count: photos.length },
        { id: "scans", label: "3D Scans", icon: Cube, count: scans.length },
        { id: "xrays", label: "X-rays", icon: ImageIcon, count: xrays.length },
        { id: "docs", label: "Documents", icon: FileText, count: documents.length },
    ], [photos.length, scans.length, xrays.length, documents.length]);

    if (total === 0) return null;

    return (
        <div
            className="bg-card rounded-2xl border border-border/70 overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
            data-testid="planner-records-card"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-5 py-3 border-b border-border/60 flex items-center gap-3 hover:bg-teal-50/40 transition-colors text-left group"
                style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                aria-expanded={open}
                data-testid="planner-records-toggle"
            >
                {number != null && (
                    <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                        {String(number).padStart(2, "0")}
                    </span>
                )}
                <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 flex-1">
                    Records Submitted
                </h3>
                <span className="text-[10.5px] text-teal-800/70 font-semibold tabular-nums">
                    {total} file{total === 1 ? "" : "s"}
                </span>
                <CaretDown
                    size={13}
                    weight="bold"
                    className={`text-teal-700 transition-transform group-hover:scale-110 ${open ? "" : "-rotate-90"}`}
                />
            </button>

            {open && (
                <>
                    {/* Tabs strip */}
                    <div className="flex items-center border-b border-border px-3 overflow-x-auto" role="tablist">
                        {tabs.map(({ id, label, icon: IconComp, count }) => {
                            const active = tab === id;
                            const empty = count === 0;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    disabled={empty}
                                    onClick={() => setTab(id)}
                                    role="tab"
                                    aria-selected={active}
                                    className={`px-3.5 py-2.5 inline-flex items-center gap-1.5 text-xs font-medium border-b-2 transition-colors shrink-0
                                        ${active
                                            ? "border-primary text-primary"
                                            : empty
                                                ? "border-transparent text-muted-foreground/40 cursor-not-allowed"
                                                : "border-transparent text-muted-foreground hover:text-foreground"
                                        }`}
                                    data-testid={`planner-records-tab-${id}`}
                                >
                                    <IconComp size={13} weight={active ? "fill" : "regular"} />
                                    {label}
                                    <span className={`text-[10px] tabular-nums ${active ? "text-primary" : "text-muted-foreground/70"}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="p-5">
                        {tab === "photos" && (
                            <PhotosPane photos={photos} newSinceInfoRequest={newSinceInfoRequest} />
                        )}
                        {tab === "scans" && (
                            <FileListPane files={scans} Icon={Cube} accent="violet"
                                newSinceInfoRequest={newSinceInfoRequest} />
                        )}
                        {tab === "xrays" && (
                            <FileListPane files={xrays} Icon={ImageIcon} accent="slate"
                                newSinceInfoRequest={newSinceInfoRequest} />
                        )}
                        {tab === "docs" && (
                            <FileListPane files={documents} Icon={FileText} accent="rose"
                                newSinceInfoRequest={newSinceInfoRequest} />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


function PhotosPane({ photos, newSinceInfoRequest }) {
    // iter98.99br — Group intraoral vs extraoral so the planner reads
    // them in clinical pairs instead of one big grid.
    const intra = photos.filter((p) => p.kind?.startsWith("intraoral"));
    const extra = photos.filter((p) => p.kind?.startsWith("extraoral"));
    const other = photos.filter((p) => !p.kind?.startsWith("intraoral") && !p.kind?.startsWith("extraoral"));
    return (
        <div className="space-y-5">
            {intra.length > 0 && <PhotoGroup label="Intraoral Photos" photos={intra} newSinceInfoRequest={newSinceInfoRequest} />}
            {extra.length > 0 && <PhotoGroup label="Extraoral Photos" photos={extra} newSinceInfoRequest={newSinceInfoRequest} />}
            {other.length > 0 && <PhotoGroup label="Other Photos" photos={other} newSinceInfoRequest={newSinceInfoRequest} />}
        </div>
    );
}


function PhotoGroup({ label, photos, newSinceInfoRequest }) {
    return (
        <div>
            <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground mb-2">{label}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((f) => {
                    const isNew = newSinceInfoRequest?.has(f.id);
                    const href = `${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`;
                    return (
                        <a
                            key={f.id}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex gap-3 p-3 rounded-lg border border-border bg-secondary/40 hover:bg-secondary transition-colors"
                            title={isNew ? "Replaced after your info-request" : f.name}
                            data-testid={isNew ? `planner-photo-replaced-${f.id}` : `planner-photo-${f.id}`}
                        >
                            <div className="relative w-20 h-20 rounded-md overflow-hidden bg-card border border-border shrink-0">
                                <img
                                    src={href}
                                    alt={FILE_KIND_LABEL[f.kind] || f.name}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                {isNew && (
                                    <span className="absolute top-1 right-1 inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded shadow-sm">
                                        <ArrowsClockwise size={7} weight="bold" /> New
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-foreground truncate">
                                    {FILE_KIND_LABEL[f.kind] || f.name}
                                </div>
                                <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">
                                    {f.size ? `${(f.size / 1024).toFixed(0)} KB` : null}
                                    {f.content_type && (
                                        <> · {f.content_type.split("/")[1]?.toUpperCase() || f.content_type}</>
                                    )}
                                </div>
                            </div>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}


function FileListPane({ files, Icon, accent = "slate", newSinceInfoRequest }) {
    if (files.length === 0) {
        return <EmptyPane label="No files in this category yet." />;
    }
    const accentColors = {
        violet: { bg: "bg-violet-50", border: "border-violet-200", icon: "text-violet-700", text: "text-violet-700" },
        slate:  { bg: "bg-slate-50", border: "border-slate-200", icon: "text-slate-700", text: "text-slate-700" },
        rose:   { bg: "bg-rose-50",   border: "border-rose-200",   icon: "text-rose-700",   text: "text-rose-700" },
    }[accent] || { bg: "bg-secondary", border: "border-border", icon: "text-muted-foreground", text: "text-muted-foreground" };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {files.map((f) => {
                const isNew = newSinceInfoRequest?.has(f.id);
                const href = `${API}/files/${f.id}?auth=${localStorage.getItem("ca_token")}`;
                return (
                    <a
                        key={f.id}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex gap-3 p-3 rounded-lg border bg-secondary/40 hover:bg-secondary transition-colors ${
                            isNew ? "border-emerald-300 bg-emerald-50/50" : "border-border"
                        }`}
                        data-testid={isNew ? `planner-file-replaced-${f.id}` : `planner-file-${f.id}`}
                    >
                        <div className={`w-20 h-20 rounded-md ${accentColors.bg} border ${accentColors.border} grid place-items-center shrink-0`}>
                            <Icon size={28} weight="duotone" className={accentColors.icon} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-foreground truncate">
                                {FILE_KIND_LABEL[f.kind] || f.name}
                            </div>
                            <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">
                                {f.size ? `${(f.size / 1024).toFixed(1)} KB` : null}
                                {f.content_type && (
                                    <> · {f.content_type.split("/")[1]?.toUpperCase() || "FILE"}</>
                                )}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{f.name}</div>
                            {isNew && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded shadow-sm mt-1">
                                    <ArrowsClockwise size={8} weight="bold" /> Replaced
                                </span>
                            )}
                        </div>
                    </a>
                );
            })}
        </div>
    );
}


function EmptyPane({ label }) {
    return <div className="text-xs text-muted-foreground/60 italic py-6 text-center">{label}</div>;
}
